import { useEffect, useMemo, useRef, useState } from 'react';
import { View, Animated, useWindowDimensions, type LayoutChangeEvent } from 'react-native';
import { wordleBoardStyles as styles } from './WordleBoard.styles';
import { WordleTile, type BoardTile } from '../WordleTile/WordleTile';
import { BOARD_TILE_LEDGE } from '../../../src/quiz/constants/warmup.constants';
import type { WordleGuessRow, WordleStatus } from '../../../src/quiz/helpers/gamesApi';

interface WordleBoardProps {
  length: number;
  maxGuesses: number;
  guesses: WordleGuessRow[];
  currentInput: string;
  status: WordleStatus;
  shakeNonce: number;
}

const BOARD_MAX_WIDTH = 336;
const GAP = 7;
const VERTICAL_PADDING = 16; // container paddingVertical (8 top + 8 bottom)
const MIN_TILE = 26;
const MAX_TILE = 60;

/** Largest tile that fits the row width. */
function tileFromWidth(length: number, available: number): number {
  if (length <= 0) return MAX_TILE;
  const width = Math.min(available, BOARD_MAX_WIDTH);
  return Math.floor((width - GAP * (length - 1)) / length);
}

/** Largest tile so all rows (each `size + ledge` tall, plus gaps) fit the measured board height. */
function tileFromHeight(maxGuesses: number, available: number): number {
  if (maxGuesses <= 0 || available <= 0) return MAX_TILE;
  const usable = available - VERTICAL_PADDING - GAP * (maxGuesses - 1);
  return Math.floor(usable / maxGuesses) - BOARD_TILE_LEDGE;
}

export function WordleBoard({ length, maxGuesses, guesses, currentInput, status, shakeNonce }: WordleBoardProps) {
  const { width } = useWindowDimensions();
  const [boardHeight, setBoardHeight] = useState(0);
  const onBoardLayout = (e: LayoutChangeEvent) => setBoardHeight(e.nativeEvent.layout.height);
  const size = useMemo(() => {
    const byWidth = tileFromWidth(length, width - 40);
    const byHeight = boardHeight > 0 ? tileFromHeight(maxGuesses, boardHeight) : byWidth;
    return Math.max(MIN_TILE, Math.min(MAX_TILE, byWidth, byHeight));
  }, [length, maxGuesses, width, boardHeight]);
  const isPlayable = status === 'NOT_STARTED' || status === 'IN_PROGRESS';
  const currentRowIndex = guesses.length;

  // Reveal only the row just committed this render — never the rows a resumed game loads with.
  // Derived synchronously (vs. state) so the row that flips to committed pops on the same render.
  const prevLen = useRef<number | null>(null);
  const revealRow = prevLen.current !== null && guesses.length > prevLen.current ? guesses.length - 1 : -1;
  useEffect(() => {
    prevLen.current = guesses.length;
  }, [guesses.length]);

  const shakeX = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (shakeNonce === 0) return;
    Animated.sequence([
      Animated.timing(shakeX, { toValue: -7, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeX, { toValue: 7, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeX, { toValue: -5, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeX, { toValue: 0, duration: 50, useNativeDriver: true }),
    ]).start();
  }, [shakeNonce, shakeX]);

  const rows = useMemo(() => {
    const out: { key: string; tiles: BoardTile[] }[] = [];
    for (let r = 0; r < maxGuesses; r += 1) {
      const committed = guesses[r];
      const isCurrent = r === currentRowIndex && isPlayable;
      const tiles: BoardTile[] = [];
      for (let i = 0; i < length; i += 1) {
        if (committed) {
          tiles.push({ letter: committed.word[i] ?? '', state: committed.marks[i] });
        } else if (isCurrent && currentInput[i]) {
          tiles.push({ letter: currentInput[i], state: 'filled' });
        } else {
          tiles.push({ letter: '', state: 'empty' });
        }
      }
      out.push({ key: `row-${r}`, tiles });
    }
    return out;
  }, [maxGuesses, guesses, currentRowIndex, isPlayable, length, currentInput]);

  return (
    <View style={styles.container} onLayout={onBoardLayout}>
      {rows.map((row, rIndex) => {
        const isCurrent = rIndex === currentRowIndex && isPlayable;
        const committed = rIndex < guesses.length;
        const revealOnMount = rIndex === revealRow;
        return (
          <Animated.View
            key={row.key}
            style={[styles.row, isCurrent ? { transform: [{ translateX: shakeX }] } : null]}
          >
            {row.tiles.map((tile, i) => (
              // Key flips when the row commits, so a just-guessed row remounts and pops in fresh.
              <WordleTile
                key={`${row.key}-${committed ? 'c' : 'e'}-${i}`}
                tile={tile}
                size={size}
                index={i}
                revealOnMount={revealOnMount}
              />
            ))}
          </Animated.View>
        );
      })}
    </View>
  );
}
